/*
Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.

Licensed under the Apache License, Version 2.0 (the "License").
You may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
*/
import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  Badge,
  Box,
  Breadcrumbs,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Switch,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import DescriptionIcon from '@mui/icons-material/Description';

import { GlossaryDialog } from '../components/glossary/GlossaryCollectionsDialog';
import { getStore, RootState } from '@ink-ai/portal/reducers';
import {
  translationActions,
  uploadAndTranslate,
} from '@ink-ai/portal/reducers/translation';
import { app } from '@ink-ai/portal/reducers/app';

import { DocumentTranslateArea } from '../components/translation/DocumentTranslateArea';
import { DocumentTranslationOptionDtoTargetLanguageEnum, DocumentTranslationApi } from '@ink-ai/insight-service-sdk';

import { Link, useBlocker, useNavigate, useSearchParams } from 'react-router-dom';
import { ReferenceStoreDialog } from '../components/reference-store/ReferenceStoreDialog';
import { PaperCard } from '../components/Common';
import { BookOpenText } from 'lucide-react';
import LibraryBooksOutlinedIcon from '@mui/icons-material/LibraryBooksOutlined';
import { makeRequest, getApi } from '@ink-ai/portal/common/requestHelper';
import { hasAnyRole, adminRolesForEnv, submissionRolesForEnv } from '@ink-ai/portal/common/authHelper';
import JSZip from 'jszip';
import { Archive } from 'libarchive.js/dist/libarchive.js';

const confirmExit = () => {
  const translation = getStore().getState().translation;
  if (translation.translationStatus === 'uploading') {
    return 'The document is still in the process of uploading. Are you sure, you want to close?';
  }
};

export const DocumentTranslation: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<any>();
  const store = getStore();
  const translation = useSelector((state: RootState) => state.translation);
  const authState = useSelector((state: RootState) => state.auth);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isAdminOrSubmission = hasAnyRole(authState.group, [...adminRolesForEnv(), ...submissionRolesForEnv()]);

  useEffect(() => {
    const modeParam = searchParams.get('mode');
    if (modeParam === 'SUBMISSION' && isAdminOrSubmission) {
      dispatch(translationActions.setTranslationMode('SUBMISSION'));
      // Submission mode has no auto-detect; ensure a concrete direction.
      if (languageDirection === 'auto') {
        handleLanguageDirectionChange('en-zh');
      }
    } else {
      dispatch(translationActions.setTranslationMode('STANDARD'));
    }
  }, [searchParams]);
  // 取消标记：用于在离开页面时停止后续上传/翻译逻辑
  const cancelRef = useRef<boolean>(false);
  const bypassGuardRef = useRef<boolean>(false);
  

  const [translationStyle, setTranslationStyle] = useState<string>('');
  const [isMergeEnabled, setIsMergeEnabled] = useState<boolean>(false);
  // 默认选择自动识别
  const [languageDirection, setLanguageDirection] = useState<string>('auto');

  interface UploadItem {
    file: File;
    name: string;
    size: number;
    type: string;
    status: 'waiting upload' | 'queueing' | 'uploaded' | 'upload failed' | 'unsupported';
    progress: number;
    selected: boolean;
    uuid?: string;
    reason?: string;
  }
  const [uploadItems, setUploadItems] = useState<UploadItem[]>([]);
  const [currentIndex, setCurrentIndex] = useState<number | null>(null);

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      !bypassGuardRef.current &&
      (translation.translationStatus === 'uploading' || currentIndex !== null) &&
      currentLocation.pathname !== nextLocation.pathname,
  );

  useEffect(() => {
    window.onbeforeunload = () => {
      if (bypassGuardRef.current) return;
      return confirmExit();
    };
    return () => {
      window.onbeforeunload = null;
      // 页面卸载时标记取消，阻止后续逻辑继续执行
      cancelRef.current = true;
    };
  }, []);

  // 默认勾选 name 为 "By default" 的词库与参考资料文件夹（进入页面时）
  useEffect(() => {
    let cancelled = false;

    const extractExplorerCollections = (resp: any): any[] => {
      // 兼容新旧返回：优先 collections，其次 items
      const candidates = [
        resp?.collections,
        resp?.data?.collections,
        resp?.data?.data?.collections,
        resp?.items,
        resp?.data?.items,
        resp?.data?.data?.items,
      ];
      for (const list of candidates) {
        if (Array.isArray(list)) return list;
      }
      return [];
    };

    const isFolderEntry = (it: any): boolean => {
      const t = String(it?.itemType ?? it?.type ?? '').toLowerCase();
      return t === 'folder';
    };

    const dedupeStringIds = (ids: any[]): string[] => {
      return Array.from(new Set((ids || []).filter((x) => typeof x === 'string')));
    };

    const preselectDefaultFolders = async () => {
      try {
        // 预选 Glossary 文件夹
        const glossaryResp: any = await makeRequest({
          method: 'GET',
          url: '/api/v1/glossary/glossaryExplorer',
          params: { page: 1, pageSize: 50, name: 'By default', status: 'Ready' },
        });
        const glossaryItems = extractExplorerCollections(glossaryResp);
        const glossaryDefaultFolders: any[] = glossaryItems
          .filter((it: any) => isFolderEntry(it) && it?.name === 'By default' && typeof it.uuid === 'string');
        const glossaryFolderIds: string[] = glossaryDefaultFolders.map((it: any) => it.uuid);
        let glossaryChildIds: string[] = [];
        for (const f of glossaryDefaultFolders) {
          const children = Array.isArray(f.collections) ? f.collections : [];
          const ids = children
            .filter((c: any) => typeof c?.uuid === 'string')
            .map((c: any) => c.uuid);
          glossaryChildIds.push(...ids);
        }
        if (glossaryChildIds.length === 0 && glossaryFolderIds.length > 0) {
          for (const folderId of glossaryFolderIds) {
            try {
              const childResp: any = await makeRequest({
                method: 'GET',
                url: '/api/v1/glossary/glossaryExplorer',
                params: { parentId: folderId, page: 1, pageSize: 200 },
              });
              const childItems = extractExplorerCollections(childResp);
              const ids = childItems
                .filter((it: any) => String(it?.itemType ?? it?.type ?? '').toLowerCase() !== 'folder' && typeof it?.uuid === 'string')
                .map((it: any) => it.uuid);
              glossaryChildIds.push(...ids);
            } catch (e) {
              console.warn('Glossary child fetch failed for folder:', folderId, e);
            }
          }
        }

        // 预选 Reference 文件夹
        const refResp: any = await makeRequest({
          method: 'GET',
          url: '/api/v1/referenceStore/referenceExplorer',
          params: { page: 1, pageSize: 50, name: 'By default', status: 'Ready' },
        });
        const refItems = extractExplorerCollections(refResp);
        const referenceDefaultFolders: any[] = refItems
          .filter((it: any) => isFolderEntry(it) && it?.name === 'By default' && typeof it.uuid === 'string');
        const referenceFolderIds: string[] = referenceDefaultFolders.map((it: any) => it.uuid);
        let referenceChildIds: string[] = [];
        for (const f of referenceDefaultFolders) {
          const children = Array.isArray(f.collections) ? f.collections : [];
          const ids = children
            .filter((c: any) => typeof c?.uuid === 'string')
            .map((c: any) => c.uuid);
          referenceChildIds.push(...ids);
        }
        if (referenceChildIds.length === 0 && referenceFolderIds.length > 0) {
          for (const folderId of referenceFolderIds) {
            try {
              const childResp: any = await makeRequest({
                method: 'GET',
                url: '/api/v1/referenceStore/referenceExplorer',
                params: { parentId: folderId, page: 1, pageSize: 200 },
              });
              const childItems = extractExplorerCollections(childResp);
              const ids = childItems
                .filter((it: any) => String(it?.itemType ?? it?.type ?? '').toLowerCase() !== 'folder' && typeof it?.uuid === 'string')
                .map((it: any) => it.uuid);
              referenceChildIds.push(...ids);
            } catch (e) {
              console.warn('Reference child fetch failed for folder:', folderId, e);
            }
          }
        }

        if (cancelled) return;

        // 如果存在匹配的文件夹，则默认勾选（包含重名的全部勾选）
        const dedupedGlossaryFolderIds = dedupeStringIds(glossaryFolderIds);
        const dedupedReferenceFolderIds = dedupeStringIds(referenceFolderIds);
        if (dedupedGlossaryFolderIds.length > 0) {
          dispatch(translationActions.setSelectedGlossariesFolders(dedupedGlossaryFolderIds));
        }
        if (dedupedReferenceFolderIds.length > 0) {
          dispatch(translationActions.setSelectedReferenceStoreFolders(dedupedReferenceFolderIds));
        }
        // 级联勾选子文件（仅限直接子项）
        const dedupedGlossaryChildIds = dedupeStringIds(glossaryChildIds);
        if (dedupedGlossaryChildIds.length > 0) {
          const union = Array.from(new Set([...(getStore().getState().translation.selectedGlossaries || []), ...dedupedGlossaryChildIds]));
          dispatch(translationActions.setSelectedGlossaries(union));
        }
        const dedupedReferenceChildIds = dedupeStringIds(referenceChildIds);
        if (dedupedReferenceChildIds.length > 0) {
          const union = Array.from(new Set([...(getStore().getState().translation.selectedReferenceStores || []), ...dedupedReferenceChildIds]));
          dispatch(translationActions.setSelectedReferenceStores(union));
        }
      } catch (e) {
        console.error('Failed to preselect "By default" folders:', e);
      }
    };

    preselectDefaultFolders();
    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  useEffect(() => {
    // Clean up on unmount
    return () => {
      dispatch(translationActions.clearAll());
    };
  }, []);

  useEffect(() => {
    // 非 auto 模式时，同步语言方向与 Redux 中的源/目标语言
    if (languageDirection === 'auto') return;
    if (
      translation.sourceLanguage ===
        DocumentTranslationOptionDtoTargetLanguageEnum.EnUs &&
      translation.targetLanguage ===
        DocumentTranslationOptionDtoTargetLanguageEnum.ZhCn
    ) {
      setLanguageDirection('en-zh');
    } else if (
      translation.sourceLanguage ===
        DocumentTranslationOptionDtoTargetLanguageEnum.ZhCn &&
      translation.targetLanguage ===
        DocumentTranslationOptionDtoTargetLanguageEnum.EnUs
    ) {
      setLanguageDirection('zh-en');
    }
  }, [translation.sourceLanguage, translation.targetLanguage, languageDirection]);

  const deriveMimeFromName = (name: string): string => {
    const ext = (name.split('.').pop() || '').toLowerCase();
    switch (ext) {
      case 'docx': return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      case 'pdf': return 'application/pdf';
      case 'json': return 'application/json';
      case 'txt': return 'text/plain';
      case 'html':
      case 'htm': return 'text/html';
      case 'xml': return 'application/xml';
      case 'xhtml': return 'application/xhtml+xml';
      case 'pptx': return 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
      case 'xlsx': return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      case 'rtf': return 'application/rtf';
      case 'md':
      case 'markdown': return 'text/markdown';
      case 'bmp': return 'image/bmp';
      case 'jpg':
      case 'jpeg': return 'image/jpeg';
      case 'png': return 'image/png';
      default: return 'application/octet-stream';
    }
  };

  const parseTar = (buffer: ArrayBuffer): File[] => {
    const out: File[] = [];
    const block = 512;
    const u8 = new Uint8Array(buffer);
    const td = new TextDecoder('utf-8');
    let off = 0;
    while (off + block <= u8.length) {
      let empty = true;
      for (let i = 0; i < block; i++) { if (u8[off + i] !== 0) { empty = false; break; } }
      if (empty) break;
      const nameRaw = u8.slice(off, off + 100);
      const name = td.decode(nameRaw).replace(/\0.*$/, '').trim();
      const sizeRaw = td.decode(u8.slice(off + 124, off + 136)).replace(/\0.*$/, '').trim();
      const size = parseInt(sizeRaw || '0', 8) || 0;
      const typeflag = u8[off + 156];
      const contentStart = off + block;
      const sizePad = Math.ceil(size / block) * block;
      if (typeflag === 0x30 || typeflag === 0 || typeflag === 32) {
        const bytes = u8.slice(contentStart, contentStart + size);
        const blob = new Blob([bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength)]);
        const mime = deriveMimeFromName(name || 'file');
        out.push(new File([blob], name || 'file', { type: mime }));
      }
      off = contentStart + sizePad;
    }
    return out;
  };

  const gunzip = async (blob: Blob): Promise<ArrayBuffer> => {
    const DS: any = (globalThis as any).DecompressionStream;
    if (!DS) throw new Error('DecompressionStream not available');
    const stream = (blob as any).stream().pipeThrough(new DS('gzip'));
    const ab = await new Response(stream).arrayBuffer();
    return ab;
  };

  const expandZipFiles = async (files: File[]): Promise<File[]> => {
    const result: File[] = [];
    for (const f of files) {
      const lower = f.name.toLowerCase();
      const ext = (lower.split('.').pop() || '').toLowerCase();
      if (ext === 'zip') {
        try {
          const zip = await JSZip.loadAsync(f);
          const entries = Object.values(zip.files);
          for (const entry of entries) {
            if ((entry as any).dir) continue;
            const content = await (entry as any).async('blob');
            const fileName = (entry as any).name || 'unnamed';
            const mime = deriveMimeFromName(fileName);
            const extracted = new File([content], fileName, { type: mime });
            result.push(extracted);
          }
        } catch (e) {
          result.push(f);
        }
        continue;
      }
      if (ext === '7z') {
        try {
          await (Archive as any).init({ workerUrl: '/assets/libarchive/worker-bundle.js' });
          const archive = await (Archive as any).open(f);
          const listing: any[] = await (archive as any).getFilesArray();
          for (const item of listing) {
            let fileObj: any = item?.file;
            if (!(fileObj instanceof File) && fileObj && typeof fileObj.extract === 'function') {
              fileObj = await fileObj.extract();
            }
            if (fileObj instanceof File) {
              const name = fileObj.name || 'unnamed';
              const mime = deriveMimeFromName(name);
              const extracted = new File([fileObj], name, { type: mime });
              result.push(extracted);
            }
          }
        } catch (e) {
          result.push(f);
        }
        continue;
      }
      if (lower.endsWith('.tar') || ext === 'tar') {
        try {
          const ab = await f.arrayBuffer();
          const filesFromTar = parseTar(ab);
          result.push(...filesFromTar);
        } catch (e) {
          result.push(f);
        }
        continue;
      }
      if (lower.endsWith('.tar.gz') || ext === 'tgz') {
        try {
          const ab = await gunzip(f);
          const filesFromTar = parseTar(ab);
          result.push(...filesFromTar);
        } catch (e) {
          result.push(f);
        }
        continue;
      }
      result.push(f);
    }
    return result;
  };

  // Accepted formats and size limits per mode. XML is SUBMISSION-only: the
  // STANDARD worker translates it node by node with a generic HTML/XML
  // translator, which is far too slow on a real define.xml and also translates
  // code values (CheckValue) that must stay untouched.
  const checkFile = (
    f: File,
    mode: string,
  ): { unsupported: boolean; reason?: string } => {
    const isSubmission = mode === 'SUBMISSION';
    const allowedExts = isSubmission
      ? ['pdf', 'docx', 'xml']
      : ['txt', 'docx', 'pptx', 'xlsx', 'rtf', 'md', 'markdown', 'bmp', 'jpg', 'jpeg', 'png', 'pdf', 'html', 'htm', 'xhtml'];
    const ext = (f.name.split('.').pop() || '').toLowerCase();
    const isAllowed = allowedExts.includes(ext);
    const sizeLimit = ext === 'txt' ? 10 * 1024 * 1024 : 50 * 1024 * 1024;
    const withinLimit = f.size <= sizeLimit;
    let reason: string | undefined;
    if (!isAllowed) {
      if (isSubmission) {
        reason = t('translation.uploadReason.formatUnsupportedSubmission', { ext: ext || t('common.na') });
      } else if (ext === 'xml') {
        reason = t('translation.uploadReason.xmlSubmissionOnly');
      } else {
        reason = t('translation.uploadReason.formatUnsupported', { ext: ext || t('common.na') });
      }
    } else if (!withinLimit) {
      reason = ext === 'txt' ? t('translation.uploadReason.sizeExceededTxt') : t('translation.uploadReason.sizeExceededOthers');
    }
    return { unsupported: !isAllowed || !withinLimit, reason };
  };

  const makeItemsFromFiles = (files: File[]): UploadItem[] => {
    return files.map((f) => {
      const { unsupported, reason } = checkFile(f, translation.translationMode);
      return {
        file: f,
        name: f.name,
        size: f.size,
        type: f.type,
        status: unsupported ? 'unsupported' : 'waiting upload',
        progress: 0,
        selected: false,
        reason,
      };
    });
  };

  const readAllEntries = (reader: any): Promise<any[]> => {
    return new Promise((resolve, reject) => {
      const entries: any[] = [];
      const read = () => {
        reader.readEntries((batch: any[]) => {
          if (!batch || batch.length === 0) resolve(entries);
          else { entries.push(...batch); read(); }
        }, reject);
      };
      read();
    });
  };

  const traverseEntry = async (entry: any): Promise<File[]> => {
    if (!entry) return [];
    if (entry.isFile) {
      return new Promise((resolve) => {
        entry.file((file: File) => resolve([file]), () => resolve([]));
      });
    }
    if (entry.isDirectory) {
      const reader = entry.createReader();
      const entries = await readAllEntries(reader);
      const results = await Promise.all(entries.map((e: any) => traverseEntry(e)));
      return results.flat();
    }
    return [];
  };

  const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const dt = event.dataTransfer;
    const itemsList = dt.items;
    let files: File[] = [];
    if (itemsList && itemsList.length) {
      const promises: Promise<File[]>[] = [];
      Array.from(itemsList).forEach((item: any) => {
        if (item.kind === 'file') {
          const entry = item.webkitGetAsEntry ? item.webkitGetAsEntry() : undefined;
          if (entry) promises.push(traverseEntry(entry));
          else {
            const f = item.getAsFile ? item.getAsFile() : null;
            if (f) promises.push(Promise.resolve([f]));
          }
        }
      });
      const res = await Promise.all(promises);
      files = res.flat();
    } else {
      files = Array.from(dt.files || []);
    }
    if (files.length > 0) {
      const expanded = await expandZipFiles(files);
      const toItems = makeItemsFromFiles(expanded);
      const existingSelected = uploadItems.filter((it) => it.selected && (it.status === 'waiting upload' || it.status === 'queueing')).length;
      let slots = Math.max(0, 10 - existingSelected);
      const adjusted = toItems.map((it) => {
        if (slots > 0 && it.status === 'waiting upload') { slots--; return { ...it, selected: true }; }
        return it;
      });
      setUploadItems((prev) => [...prev, ...adjusted]);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const handleFolderSelectChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = Array.from(event.target.files || []);
    expandZipFiles(fileList).then((expanded) => {
      const toItems = makeItemsFromFiles(expanded);
      const existingSelected = uploadItems.filter((it) => it.selected && (it.status === 'waiting upload' || it.status === 'queueing')).length;
      let slots = Math.max(0, 10 - existingSelected);
      const adjusted = toItems.map((it) => {
        if (slots > 0 && it.status === 'waiting upload') { slots--; return { ...it, selected: true }; }
        return it;
      });
      setUploadItems((prev) => [...prev, ...adjusted]);
    });
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = Array.from(event.target.files || []);
    expandZipFiles(fileList).then((expanded) => {
      const toItems = makeItemsFromFiles(expanded);
      const existingSelected = uploadItems.filter((it) => it.selected && (it.status === 'waiting upload' || it.status === 'queueing')).length;
      let slots = Math.max(0, 10 - existingSelected);
      const adjusted = toItems.map((it) => {
        if (slots > 0 && it.status === 'waiting upload') { slots--; return { ...it, selected: true }; }
        return it;
      });
      setUploadItems((prev) => [...prev, ...adjusted]);
    });
  };

  const handleSwapLanguages = () => {
    const newSource = translation.targetLanguage;
    const newTarget = translation.sourceLanguage;
    dispatch(translationActions.setSourceLanguage(newSource));
    dispatch(translationActions.setTargetLanguage(newTarget));
  };

  const handleGlossaryButtonClick = () => {
    dispatch(translationActions.setGlossaryDialogOpen(true));
  };

  const handleGlossaryDialogClose = () => {
    dispatch(translationActions.setGlossaryDialogOpen(false));
  };

  const handleGlossaryApply = (
    selectedGlossaryUuids: string[],
    selectedGlossaryFolderUuids: string[],
  ) => {
    const dedupedGlossaryUuids = Array.from(new Set((selectedGlossaryUuids || []).filter((x) => typeof x === 'string')));
    const dedupedGlossaryFolderUuids = Array.from(new Set((selectedGlossaryFolderUuids || []).filter((x) => typeof x === 'string')));
    dispatch(translationActions.setSelectedGlossaries(dedupedGlossaryUuids));
    dispatch(translationActions.setSelectedGlossariesFolders(dedupedGlossaryFolderUuids));
    dispatch(translationActions.setGlossaryDialogOpen(false));
  };

  const handleReferenceStoreButtonClick = () => {
    dispatch(translationActions.setReferenceStoreDialogOpen(true));
  };

  const handleReferenceStoreDialogClose = () => {
    dispatch(translationActions.setReferenceStoreDialogOpen(false));
  };

  const handleReferenceStoreApply = (
    selectedReferenceUuids: string[],
    selectedReferenceFolderUuids: string[],
  ) => {
    const dedupedReferenceUuids = Array.from(new Set((selectedReferenceUuids || []).filter((x) => typeof x === 'string')));
    const dedupedReferenceFolderUuids = Array.from(new Set((selectedReferenceFolderUuids || []).filter((x) => typeof x === 'string')));
    dispatch(translationActions.setSelectedReferenceStores(dedupedReferenceUuids));
    dispatch(translationActions.setSelectedReferenceStoreFolders(dedupedReferenceFolderUuids));
    dispatch(translationActions.setReferenceStoreDialogOpen(false));
  };

  const handleTranslate = async () => {
    try {
      // 每次开始新的上传/翻译前，重置取消标记，允许流程继续
      cancelRef.current = false;
      const selectedIndices = uploadItems
        .map((it, idx) => (it.selected && (it.status === 'waiting upload' || it.status === 'queueing')) ? idx : -1)
        .filter((i) => i >= 0);
      if (selectedIndices.length > 10) {
        store.dispatch(
          app.actions.setGlobalMessage({
            message: t('translation.document.maxFiles', 'You can upload up to 10 files at a time'),
            status: 'error',
          }),
        );
        return;
      }
      if (selectedIndices.length === 0) return;
      // 标记选中的文件进入队列（仅等待上传的进入 queueing）
      setUploadItems((prev) => prev.map((it, i) => selectedIndices.includes(i) && it.status === 'waiting upload' ? { ...it, status: 'queueing' } : it));
      let anyFailed = false;
      for (const idx of selectedIndices) {
        // 若用户已触发取消，直接中断后续循环
        if (cancelRef.current) { anyFailed = true; break; }
        const item = uploadItems[idx];
        if (!(item.status === 'waiting upload' || item.status === 'queueing')) continue;
        setCurrentIndex(idx);
        dispatch(translationActions.setLoading(true));
        dispatch(translationActions.setSelectedFile(item.file));
        dispatch(translationActions.setFileName(item.name));
        dispatch(translationActions.setFileSize(item.size));
        dispatch(translationActions.setTimeStamp(Date.now()));
        const resultAction = await dispatch(
          languageDirection === 'auto' ? uploadAndTranslate({ type: 'auto' }) : uploadAndTranslate()
        );
        // 若此时用户点击离开，停止处理后续逻辑
        if (cancelRef.current) { anyFailed = true; break; }
        if (uploadAndTranslate.fulfilled.match(resultAction)) {
          const uuid = getStore().getState().translation.uuid || undefined;
          setUploadItems((prev) => prev.map((it, i) => i === idx ? { ...it, uuid, status: 'uploaded', selected: false } : it));
        } else {
          const payload: any = (resultAction as any)?.payload;
          const reason = (payload && payload.message) || (resultAction as any)?.error?.message || '未知错误';
          setUploadItems((prev) => prev.map((it, i) => i === idx ? { ...it, status: 'upload failed', selected: false, reason } : it));
          anyFailed = true;
        }
        dispatch(translationActions.setLoading(false));
        setCurrentIndex(null);
      }
      if (!anyFailed) {
        bypassGuardRef.current = true;
        window.onbeforeunload = null;
        navigate('/translate-tasks');
      }
    } catch (error) {
      console.error(error);
      dispatch(translationActions.setLoading(false));
    }
  };

  // 清空列表时也需要取消后续上传/翻译逻辑，并清理服务端未完成任务
  const handleClearAllClick = async () => {
    try {
      // 标记取消，阻止后续文件的上传与翻译
      cancelRef.current = true;
      const state = getStore().getState() as any;
      const { uuid, translationStatus, abortController } = state.translation || {};
      // 1) 终止正在进行的上传
      if (translationStatus === 'uploading' && abortController) {
        try { abortController.abort(); } catch {}
      }
      // 清空当前处理索引，避免残留状态影响后续操作
      setCurrentIndex(null);
      // 2) 收集需要删除的服务端任务 id（当前 uuid 以及本地未完成项的 uuid）
      const ids = new Set<string>();
      if (uuid) ids.add(uuid);
      (uploadItems || [])
        .filter((it) => it.uuid && it.status !== 'uploaded')
        .forEach((it) => ids.add(it.uuid as string));
      // 3) 删除这些服务端任务
      if (ids.size > 0) {
        const translateApi = await getApi(DocumentTranslationApi);
        for (const id of ids) {
          try { await translateApi.deleteDocumentTranslation(id); } catch (e) { console.error(e); }
        }
      }
      // 4) 前端状态清理：清空列表并重置与文件相关的 Redux 状态（不改变语言方向）
      setUploadItems([]);
      dispatch(translationActions.removeFile());
      dispatch(translationActions.setAbortController(null));
    } catch (err) {
      console.error('Clear list cleanup failed:', err);
    }
  };

  const formatFileSize = (size: number) => {
    if (size < 1024) return `${size} bytes`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(2)} kb`;
    if (size < 1024 * 1024 * 1024)
      return `${(size / (1024 * 1024)).toFixed(2)} mb`;
    return `${(size / (1024 * 1024 * 1024)).toFixed(2)} gb`;
  };



  const handleTranslationModeChange = (mode: string) => {
    dispatch(translationActions.setTranslationMode(mode as any));
    // Re-check files that are not uploaded yet against the new mode, otherwise a
    // file picked under one mode (e.g. XML under SUBMISSION) would be submitted
    // under the other one, where it is not accepted.
    setUploadItems((prev) =>
      prev.map((it) => {
        if (it.status !== 'waiting upload' && it.status !== 'unsupported') return it;
        const { unsupported, reason } = checkFile(it.file, mode);
        return {
          ...it,
          status: unsupported ? 'unsupported' : 'waiting upload',
          reason,
          selected: unsupported ? false : it.selected,
        };
      }),
    );
    // Submission mode only supports English -> Chinese (no auto-detect, no
    // zh->en). Force en-zh whenever the current direction isn't already it, so
    // the request carries explicit languages and the dropdown never stays on a
    // now-hidden option.
    if (mode === 'SUBMISSION' && languageDirection !== 'en-zh') {
      handleLanguageDirectionChange('en-zh');
    }
  };

  const handleLanguageDirectionChange = (direction: string) => {
    setLanguageDirection(direction);
    if (direction === 'auto') {
      // 自动模式下不更新源/目标语言，后端按 type:auto 处理
      return;
    } else if (direction === 'en-zh') {
      dispatch(
        translationActions.setSourceLanguage(
          DocumentTranslationOptionDtoTargetLanguageEnum.EnUs,
        ),
      );
      dispatch(
        translationActions.setTargetLanguage(
          DocumentTranslationOptionDtoTargetLanguageEnum.ZhCn,
        ),
      );
    } else if (direction === 'zh-en') {
      dispatch(
        translationActions.setSourceLanguage(
          DocumentTranslationOptionDtoTargetLanguageEnum.ZhCn,
        ),
      );
      dispatch(
        translationActions.setTargetLanguage(
          DocumentTranslationOptionDtoTargetLanguageEnum.EnUs,
        ),
      );
    }
  };

  // 上传页不再轮询翻译状态

  return (
    <Container maxWidth="lg" sx={{ mt: 1, mb: 1 }}>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
        <Link to="/">{t('translation.navigation.home')}</Link>
        <Link to="/translate-tasks">
          {t('translation.navigation.translateTasks')}
        </Link>
        <Typography color="textPrimary">
          {t('translation.navigation.new')}
        </Typography>
        {/* <Typography color="textPrimary">
          {t('translation.navigation.documentTranslate')}
        </Typography> */}
      </Breadcrumbs>
      <PaperCard>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {/* Language Direction Dropdown */}
            <FormControl size="small" sx={{ minWidth: 200, mr: 2 }}>
              <InputLabel id="language-direction-label">
                {t('translation.languageDirection.label')}
              </InputLabel>
              <Select
                labelId="language-direction-label"
                value={languageDirection}
                label={t('translation.languageDirection.label')}
                onChange={(e) => handleLanguageDirectionChange(e.target.value)}
                sx={{ '& .MuiSelect-select, & .MuiInputBase-input': { textAlign: 'left' } }}
              >
                {/* Submission mode does not support auto-detect: the submission
                    worker requires explicit source/target languages, so hide
                    the auto option when Submission mode is selected. */}
                {translation.translationMode !== 'SUBMISSION' && (
                  <MenuItem value="auto">
                    {t('translation.languages.autoDetect')}
                  </MenuItem>
                )}
                <MenuItem value="en-zh">
                  {t('translation.languages.enUs')} → {t('translation.languages.zhCn')}
                </MenuItem>
                {/* Submission mode only supports English -> Chinese (the
                    submission model is unreliable for zh->en), so hide the
                    zh->en option when Submission mode is selected. */}
                {translation.translationMode !== 'SUBMISSION' && (
                  <MenuItem value="zh-en">
                    {t('translation.languages.zhCn')} → {t('translation.languages.enUs')}
                  </MenuItem>
                )}
              </Select>
            </FormControl>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {/* Style Selection Dropdown */}
            {/* <FormControl variant="standard" size="small" sx={{ minWidth: 120, mr: 2 }}>
              <InputLabel id="translation-style-label">
                {t('translation.buttons.style')}
              </InputLabel>
              <Select
                labelId="translation-style-label"
                id="translation-style-select"
                value={translationStyle}
                label="Style"
                onChange={(e) => setTranslationStyle(e.target.value)}
                sx={{ '& .MuiSelect-select, & .MuiInputBase-input': { textAlign: 'left' } }}
              >
                <MenuItem value="Colloquial">
                  {t('translation.style.colloquial')}
                </MenuItem>
                <MenuItem value="Officialization">
                  {t('translation.style.officialization')}
                </MenuItem>
                <MenuItem value="and...">
                  {t('translation.style.and')}
                </MenuItem>
              </Select>
            </FormControl> */}
            
            {isAdminOrSubmission && (
              <FormControl size="small" sx={{ mr: 2 }}>
                <RadioGroup
                  row
                  value={translation.translationMode}
                  onChange={(e) => handleTranslationModeChange(e.target.value)}
                >
                  <FormControlLabel value="STANDARD" control={<Radio size="small" />} label={t('translation.translationMode.standard')} />
                  <FormControlLabel value="SUBMISSION" control={<Radio size="small" />} label={t('translation.translationMode.submission')} />
                </RadioGroup>
              </FormControl>
            )}
            <Badge
              badgeContent={translation.selectedGlossaries.length}
              color="error"
            >
              <Button
                onClick={handleGlossaryButtonClick}
                variant="outlined"
                disabled={translation.translationStatus === 'uploading'}
                sx={{ ml: 2, textTransform: 'none', minWidth: 130, gap: '6px' }}
              >
                <BookOpenText/>
                {t('translation.buttons.glossary')}
              </Button>
            </Badge>
            
            <Badge
              badgeContent={translation.selectedReferenceStores.length}
              color="error"
            >
              <Button
                onClick={handleReferenceStoreButtonClick}
                variant="outlined"
                disabled={translation.translationStatus === 'uploading'}
                sx={{ ml: 2, textTransform: 'none', minWidth: 130, gap: '6px' }}
              >
                <LibraryBooksOutlinedIcon/>
                {t('translation.buttons.reference')}
              </Button>
            </Badge>
          </Box>
        </Box>
        <DocumentTranslateArea
          translation={translation}
          items={uploadItems}
          handleDrop={handleDrop}
          handleDragOver={handleDragOver}
          handleFileChange={handleFileChange}
          handleFolderSelectChange={handleFolderSelectChange}
          handleTranslate={handleTranslate}
          handleRemoveItem={(index) => setUploadItems((prev) => prev.filter((_, i) => i !== index))}
          handleToggleSelect={(index, selected) => setUploadItems((prev) => prev.map((it, i) => i === index ? { ...it, selected } : it))}
          handleClearAll={handleClearAllClick}
          formatFileSize={formatFileSize}
        />
      </PaperCard>

      <GlossaryDialog
        open={translation.glossaryDialogOpen}
        onClose={handleGlossaryDialogClose}
        onApply={handleGlossaryApply}
        initialSelectedGlossaries={translation.selectedGlossaries}
        initialSelectedFolders={translation.selectedGlossariesFolders}
        cascadeSelection
        resetSelectionOnFolderNav={false}
      />

      <ReferenceStoreDialog
        open={translation.referenceStoreDialogOpen}
        onClose={handleReferenceStoreDialogClose}
        onApply={handleReferenceStoreApply}
        resetSelectionOnFolderNav={false}
        initialSelectedReferenceStores={translation.selectedReferenceStores}
        initialSelectedFolders={translation.selectedReferenceStoreFolders}
        cascadeSelection
      />

      <Dialog open={blocker.state === 'blocked'}>
        <DialogTitle>{t('translation.dialog.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('translation.dialog.content')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={async () => {
              try {
                // 标记取消，阻止后续文件的上传与翻译
                cancelRef.current = true;
                const state = getStore().getState() as any;
                const { uuid, translationStatus, abortController } = state.translation || {};
                // 1) 终止正在进行的上传
                if (translationStatus === 'uploading' && abortController) {
                  try { abortController.abort(); } catch {}
                }
                // 2) 收集需要删除的服务端任务 id（当前 uuid 以及本地未完成项的 uuid）
                const ids = new Set<string>();
                if (uuid) ids.add(uuid);
                (uploadItems || [])
                  .filter((it) => it.uuid && it.status !== 'uploaded')
                  .forEach((it) => ids.add(it.uuid as string));
                // 3) 删除这些服务端任务
                if (ids.size > 0) {
                  const translateApi = await getApi(DocumentTranslationApi);
                  for (const id of ids) {
                    try { await translateApi.deleteDocumentTranslation(id); } catch (e) { console.error(e); }
                  }
                }
                // 4) 前端状态清理：标记队列/待上传为失败，关闭 loading
                setUploadItems((prev) => prev.map((it) => {
                  const toFailById = it.uuid ? ids.has(it.uuid) : false;
                  const toFailByQueue = (it.status === 'queueing' || it.status === 'waiting upload') && it.selected;
                  return (toFailById || toFailByQueue)
                    ? { ...it, status: 'upload failed', selected: false, reason: t('translation.status.failed') }
                    : it;
                }));
                dispatch(translationActions.setTranslationStatus('failed'));
                dispatch(translationActions.setLoading(false));
              } catch (err) {
                console.error('Leave during upload cleanup failed:', err);
              } finally {
                blocker.proceed();
              }
            }}
          >
            {t('translation.dialog.leave')}
          </Button>
          <Button
            onClick={() => blocker.reset()}
            color="primary"
            variant="contained"
            autoFocus
          >
            {t('translation.dialog.stay')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};
